import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIdBadge, faPlus, faXmark, faCheckCircle, faCircleExclamation, faClock, faEye } from '@fortawesome/free-solid-svg-icons';
import TeacherCard, { LEVELS, DEFAULT_FIELD } from './TeacherCard';
import './PublicProfileForm.css';

/* =========================================================
   Teacher › Settings › "My public profile" ("Meet our teachers" on the home page).
   Studies (up to 2), one sentence about them, show my photo, show me on the home page.
   A live preview shows the card exactly as the home page will. Goes live after the admin approves.
   Backend: /api/teacher/public-profile (backend/api/Teachers/PublicProfile.js)
   ========================================================= */
const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const ABOUT_MAX = 150;

const newStudy = () => ({ level: 'bachelor', field: DEFAULT_FIELD.bachelor, status: 'graduated' });

const STATUS = {
  pending: { icon: faClock, tone: 'wait', text: 'Waiting for admin approval' },
  approved: { icon: faCheckCircle, tone: 'ok', text: 'Approved. You’re on the home page' },
  rejected: { icon: faCircleExclamation, tone: 'bad', text: 'The admin asked for a change' },
  hidden: { icon: faEye, tone: 'off', text: 'Not shown on the home page' },
  removed: { icon: faEye, tone: 'off', text: 'Removed from the home page by the admin. Save again to send it back.' },
};

const PublicProfileForm = () => {
  const [info, setInfo] = useState(null); // { firstName, photo, years, status, live, rejectNote }
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/api/teacher/public-profile`, auth())
      .then((res) => {
        if (!alive) return;
        const d = res.data || {};
        const draft = {
          studies: d.draft?.studies?.length ? d.draft.studies : [newStudy()],
          about: d.draft?.about || '',
          showPhoto: !!d.draft?.showPhoto,
          show: !!d.draft?.show,
        };
        setInfo(d);
        setForm(draft);
        setSaved(JSON.stringify(draft));
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, []);

  if (failed) {
    return (
      <section className="rmtset-card rmtpp">
        <h2 className="rmtset-card-title">
          <FontAwesomeIcon icon={faIdBadge} /> My public profile
        </h2>
        <p className="rmtpp-note">Couldn’t load your public profile. Refresh the page to try again.</p>
      </section>
    );
  }
  if (!form || !info) return null;

  const setStudy = (i, patch) =>
    setForm((f) => ({
      ...f,
      studies: f.studies.map((s, j) => {
        if (j !== i) return s;
        const next = { ...s, ...patch };
        // new level: suggest its usual field if the teacher hadn't typed their own
        if (patch.level && (!s.field || s.field === DEFAULT_FIELD[s.level])) next.field = DEFAULT_FIELD[patch.level];
        return next;
      }),
    }));

  const changed = JSON.stringify(form) !== saved;
  const hasPhoto = !!info.photo;
  const preview = {
    name: `Teacher ${info.firstName || 'You'}`,
    initial: (info.firstName || 'T').charAt(0),
    photo: form.showPhoto && hasPhoto ? info.photo : null,
    studies: form.studies,
    about: form.about,
    years: info.years,
    badges: info.badges || [],
  };
  const status = STATUS[info.status];

  const save = async (e) => {
    e.preventDefault();
    setMsg(null);
    const studies = form.studies.filter((s) => s.level && s.field.trim());
    if (form.show && (!studies.length || !form.about.trim())) {
      setMsg({ type: 'error', text: 'Add your studies and one sentence about you to show your profile.' });
      return;
    }
    setSaving(true);
    try {
      const body = { ...form, studies, showPhoto: form.showPhoto && hasPhoto };
      const res = await axios.put(`${API}/api/teacher/public-profile`, body, auth());
      const st = res.data?.status;
      setInfo((x) => ({ ...x, status: st, rejectNote: '', live: st === 'hidden' ? false : x.live }));
      setSaved(JSON.stringify(form));
      setMsg({
        type: 'success',
        text: st === 'pending' ? 'Sent. It shows on the home page once the admin approves it.' : 'Saved. You’re not shown on the home page.',
      });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Couldn’t save. Try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="rmtset-card rmtpp" onSubmit={save} noValidate aria-labelledby="rmtpp-title">
      <h2 id="rmtpp-title" className="rmtset-card-title">
        <FontAwesomeIcon icon={faIdBadge} /> My public profile
      </h2>
      <p className="rmtpp-note">
        Parents see this card in “Meet our teachers” on the RoboMind home page, with your first name only. It goes live after the
        admin approves it.
      </p>

      {status && (
        <p className={`rmtpp-status is-${status.tone}`}>
          <FontAwesomeIcon icon={status.icon} /> {status.text}
          {info.status === 'pending' && info.live ? ' (your approved card stays on the home page until then)' : ''}
          {info.status === 'rejected' && info.rejectNote ? `: “${info.rejectNote}”` : ''}
        </p>
      )}

      <div className="rmtpp-grid">
        <div className="rmtpp-fields">
          <fieldset className="rmtpp-group">
            <legend className="rmtpp-label">My studies</legend>
            {form.studies.map((s, i) => (
              <div key={i} className="rmtpp-study">
                <select
                  className="rmtset-input"
                  value={s.level}
                  onChange={(e) => setStudy(i, { level: e.target.value })}
                  aria-label={`Study ${i + 1}: level`}
                >
                  {LEVELS.map(([v, label]) => (
                    <option key={v} value={v}>
                      {label}
                    </option>
                  ))}
                </select>
                <input
                  className="rmtset-input"
                  value={s.field}
                  maxLength={60}
                  onChange={(e) => setStudy(i, { field: e.target.value })}
                  placeholder="Computer Science"
                  aria-label={`Study ${i + 1}: field`}
                />
                <div className="rmtpp-seg" role="radiogroup" aria-label={`Study ${i + 1}: status`}>
                  {[
                    ['graduated', 'Graduated'],
                    ['studying', 'Studying now'],
                  ].map(([v, label]) => (
                    <button
                      key={v}
                      type="button"
                      role="radio"
                      aria-checked={s.status === v}
                      className={s.status === v ? 'is-on' : ''}
                      onClick={() => setStudy(i, { status: v })}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {i > 0 && (
                  <button
                    type="button"
                    className="rmtpp-remove"
                    onClick={() => setForm((f) => ({ ...f, studies: f.studies.filter((_, j) => j !== i) }))}
                    aria-label="Remove this study"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                )}
              </div>
            ))}
            {form.studies.length < 2 && (
              <button
                type="button"
                className="rmtpp-add"
                onClick={() => setForm((f) => ({ ...f, studies: [...f.studies, { level: 'master', field: DEFAULT_FIELD.master, status: 'studying' }] }))}
              >
                <FontAwesomeIcon icon={faPlus} /> Add another (e.g. a master’s)
              </button>
            )}
          </fieldset>

          <div className="rmtpp-group">
            <label className="rmtpp-label" htmlFor="rmtpp-about">
              About me <small>(one sentence)</small>
            </label>
            <textarea
              id="rmtpp-about"
              className="rmtset-input rmtpp-about"
              rows={3}
              maxLength={ABOUT_MAX}
              value={form.about}
              onChange={(e) => setForm((f) => ({ ...f, about: e.target.value }))}
              placeholder="I help kids turn their ideas into real games and apps."
            />
            <small className="rmtpp-count">
              {form.about.length}/{ABOUT_MAX}
            </small>
          </div>

          <label className={`rmtpp-check ${hasPhoto ? '' : 'is-off'}`}>
            <input
              type="checkbox"
              checked={form.showPhoto && hasPhoto}
              disabled={!hasPhoto}
              onChange={(e) => setForm((f) => ({ ...f, showPhoto: e.target.checked }))}
            />
            <span>
              Show my profile picture
              {!hasPhoto && <small>Add a profile photo above first. Without it, the card shows your initial.</small>}
            </span>
          </label>

          <label className="rmtpp-check">
            <input type="checkbox" checked={form.show} onChange={(e) => setForm((f) => ({ ...f, show: e.target.checked }))} />
            <span>Show me on the RoboMind home page</span>
          </label>

          {info.years ? null : (
            <p className="rmtpp-hint">“Years at RoboMind” shows by itself once you have taught here for 1 year.</p>
          )}
          <p className="rmtpp-hint">Your best badges (up to 3) show on your card by themselves when RoboMind gives them to you.</p>
        </div>

        <div className="rmtpp-preview">
          <span className="rmtpp-label">Preview on the home page</span>
          <div className="rmtpp-preview-box">
            <TeacherCard t={preview} />
          </div>
        </div>
      </div>

      {msg && (
        <p className={`rmtset-msg is-${msg.type}`} role={msg.type === 'error' ? 'alert' : 'status'}>
          <FontAwesomeIcon icon={msg.type === 'success' ? faCheckCircle : faCircleExclamation} /> {msg.text}
        </p>
      )}

      <div className="rmtset-actions">
        <button type="submit" className="rmtset-btn" disabled={saving || !changed}>
          {saving ? 'Saving…' : form.show ? 'Send for approval' : 'Save'}
        </button>
      </div>
    </form>
  );
};

export default PublicProfileForm;
